import type { TestResult } from '../../tests/test-runner/test-runner.model';
import { buildAlignedFormula } from '../measures/measures-formula.util';
import { STAT_KEY_TO_LATEX, type TestMeasureId } from './test-measures.model';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function buildTestMeasureFormula(id: TestMeasureId, result: TestResult): string {
  switch (id) {
    case 'testStatistic':
      return buildTestStatFormula(result);
    case 'degreesOfFreedom':
      return buildDfFormula(result);
    case 'criticalValue':
      return buildCriticalValueFormula(result);
    case 'pValue':
      return buildPValueFormula(result);
  }
}

// ---------------------------------------------------------------------------
// Test statistic
// ---------------------------------------------------------------------------

function buildTestStatFormula(r: TestResult): string {
  const stats = r.sampleStats;

  switch (r.testType) {
    case 'one-sample-t': {
      const xBar = fmtNum(stats['x\u0304']);
      const s = fmtNum(stats['s']);
      const n = fmtNum(stats['n']);
      const mu0 = fmtNum(r.nullMean ?? 0);
      const symbolic = '$t = \\dfrac{\\bar{x} - \\mu_0}{s / \\sqrt{n}}$';
      const pluggedIn = `$t = \\dfrac{${xBar} - ${mu0}}{${s} / \\sqrt{${n}}} = ${highlight(r.testStatistic)}$`;
      return buildAlignedFormula(symbolic, pluggedIn);
    }

    case 'two-sample-t': {
      const x1 = fmtNum(stats['x\u0304\u2081']);
      const x2 = fmtNum(stats['x\u0304\u2082']);
      const s1 = fmtNum(stats['s\u2081']);
      const s2 = fmtNum(stats['s\u2082']);
      const n1 = fmtNum(stats['n\u2081']);
      const n2 = fmtNum(stats['n\u2082']);
      const symbolic = '$t = \\dfrac{\\bar{x}_1 - \\bar{x}_2}{\\sqrt{\\frac{s_1^2}{n_1} + \\frac{s_2^2}{n_2}}}$';
      const pluggedIn = `$t = \\dfrac{${x1} - ${x2}}{\\sqrt{\\frac{${s1}^2}{${n1}} + \\frac{${s2}^2}{${n2}}}} = ${highlight(
        r.testStatistic,
      )}$`;
      return buildAlignedFormula(symbolic, pluggedIn);
    }

    case 'paired-t': {
      const dBar = fmtNum(stats['d\u0304']);
      const sd = fmtNum(stats['s_d']);
      const n = fmtNum(stats['n']);
      const symbolic = '$t = \\dfrac{\\bar{d}}{s_d / \\sqrt{n}}$';
      const pluggedIn = `$t = \\dfrac{${dBar}}{${sd} / \\sqrt{${n}}} = ${highlight(r.testStatistic)}$`;
      return buildAlignedFormula(symbolic, pluggedIn);
    }

    case 'chi-squared-independence':
    case 'chi-squared-gof': {
      const symbolic = '$\\chi^2 = \\sum \\dfrac{(O_i - E_i)^2}{E_i}$';
      const pluggedIn = `$\\chi^2 = ${highlight(r.testStatistic)}$`;
      return buildAlignedFormula(symbolic, pluggedIn);
    }

    case 'sign-test': {
      const sPlus = fmtNum(stats['S+']);
      return `$S^+ = ${highlight(Number(sPlus))}$`;
    }
  }
}

// ---------------------------------------------------------------------------
// Degrees of freedom
// ---------------------------------------------------------------------------

function buildDfFormula(r: TestResult): string {
  const stats = r.sampleStats;

  switch (r.testType) {
    case 'one-sample-t': {
      const n = fmtNum(stats['n']);
      const symbolic = '$\\text{df} = n - 1$';
      const pluggedIn = `$\\text{df} = ${n} - 1 = ${highlight(r.degreesOfFreedom)}$`;
      return buildAlignedFormula(symbolic, pluggedIn);
    }

    case 'paired-t': {
      const n = fmtNum(stats['n']);
      const symbolic = '$\\text{df} = n - 1$';
      const pluggedIn = `$\\text{df} = ${n} - 1 = ${highlight(r.degreesOfFreedom)}$`;
      return buildAlignedFormula(symbolic, pluggedIn);
    }

    case 'two-sample-t': {
      const symbolic = '$\\text{df} = \\text{Welch–Satterthwaite approximation}$';
      const pluggedIn = `$\\text{df} = ${highlight(r.degreesOfFreedom)}$`;
      return buildAlignedFormula(symbolic, pluggedIn);
    }

    case 'chi-squared-gof': {
      const k = fmtNum(stats['k'] ?? 0);
      const symbolic = '$\\text{df} = k - 1$';
      const pluggedIn = `$\\text{df} = ${k} - 1 = ${highlight(r.degreesOfFreedom)}$`;
      return buildAlignedFormula(symbolic, pluggedIn);
    }

    case 'chi-squared-independence': {
      const symbolic = '$\\text{df} = (r - 1)(c - 1)$';
      const pluggedIn = `$\\text{df} = ${highlight(r.degreesOfFreedom)}$`;
      return buildAlignedFormula(symbolic, pluggedIn);
    }

    default:
      return `$\\text{df} = ${highlight(r.degreesOfFreedom)}$`;
  }
}

// ---------------------------------------------------------------------------
// Critical value
// ---------------------------------------------------------------------------

function buildCriticalValueFormula(r: TestResult): string {
  const alpha = r.alpha;
  const df = fmtNum(r.degreesOfFreedom);
  const stat = fmtNum(r.testStatistic);
  const region = r.rejectNull ? '\\text{rejection region}' : '\\text{acceptance region}';

  if (r.distributionType === 't') {
    if (r.tailType === 'two') {
      const cv = fmtNum(r.criticalValues[1] ?? r.criticalValues[0]);
      const absStat = fmtNum(Math.abs(r.testStatistic));
      const op = r.rejectNull ? '>' : '<';
      return [
        `From the $t$-distribution table with $\\text{df} = ${df}$, $\\alpha = ${alpha}$:`,
        `$$t^*_{${alpha / 2},\\,${df}} = \\pm ${highlight(Number(cv))}$$`,
        `$$|t| = ${absStat} ${op} ${cv} = t^* \\implies ${region}$$`,
      ].join('\n\n');
    }

    const cv = fmtNum(r.criticalValues[0]);
    const sign = r.tailType === 'left' ? '-' : '';
    const op = r.tailType === 'right' ? (r.rejectNull ? '>' : '<') : r.rejectNull ? '<' : '>';
    return [
      `From the $t$-distribution table with $\\text{df} = ${df}$, $\\alpha = ${alpha}$:`,
      `$$t^*_{${alpha},\\,${df}} = ${sign}${highlight(Number(cv))}$$`,
      `$$t = ${stat} ${op} ${sign}${cv} = t^* \\implies ${region}$$`,
    ].join('\n\n');
  }

  if (r.distributionType === 'chi-squared') {
    const cv = fmtNum(r.criticalValues[0]);
    const op = r.rejectNull ? '>' : '<';
    return [
      `From the $\\chi^2$-distribution table with $\\text{df} = ${df}$, $\\alpha = ${alpha}$:`,
      `$$\\chi^2_{\\text{crit}} = ${highlight(Number(cv))}$$`,
      `$$\\chi^2 = ${stat} ${op} ${cv} = \\chi^2_{\\text{crit}} \\implies ${region}$$`,
    ].join('\n\n');
  }

  // Binomial — show rejection region boundary
  const cv = fmtNum(r.criticalValues[0]);
  return `$c = ${highlight(Number(cv))}$`;
}

// ---------------------------------------------------------------------------
// p-value
// ---------------------------------------------------------------------------

function buildPValueFormula(r: TestResult): string {
  const stat = fmtNum(r.testStatistic);
  const df = fmtNum(r.degreesOfFreedom);
  const pVal = r.pValue < 0.0001 ? '< 0.0001' : `= ${fmtP(r.pValue)}`;
  const pCompare = r.pValue < 0.0001 ? '< 0.0001' : `= ${fmtP(r.pValue)}`;
  const op = r.rejectNull ? '<' : '>';
  const decision = r.rejectNull ? '\\text{Reject } H_0' : '\\text{Fail to reject } H_0';

  // Build an aligned block: symbolic → plugged-in → result → comparison
  let symbolicRhs: string;
  let pluggedRhs: string;

  if (r.distributionType === 't') {
    if (r.tailType === 'two') {
      const absStat = fmtNum(Math.abs(r.testStatistic));
      symbolicRhs = '2 \\cdot P(T_{\\text{df}} > |t|)';
      pluggedRhs = `2 \\cdot P(T_{${df}} > ${absStat})`;
    } else if (r.tailType === 'left') {
      symbolicRhs = 'P(T_{\\text{df}} < t)';
      pluggedRhs = `P(T_{${df}} < ${stat})`;
    } else {
      symbolicRhs = 'P(T_{\\text{df}} > t)';
      pluggedRhs = `P(T_{${df}} > ${stat})`;
    }
  } else if (r.distributionType === 'chi-squared') {
    symbolicRhs = 'P(\\chi^2_{\\text{df}} > \\chi^2)';
    pluggedRhs = `P(\\chi^2_{${df}} > ${stat})`;
  } else {
    // binomial / sign test — no CDF breakdown
    return `$$p ${pVal}$$\n\n$$p ${pCompare} ${op} \\alpha = ${r.alpha} \\implies ${decision}$$`;
  }

  return [
    `$$\\begin{aligned} p &= ${symbolicRhs} \\\\ &= ${pluggedRhs} \\\\ &${pVal} \\end{aligned}$$`,
    `$$p ${pCompare} ${op} \\alpha = ${r.alpha} \\implies ${decision}$$`,
  ].join('\n\n');
}

// ---------------------------------------------------------------------------
// Sample stat formula (symbolic only — no raw data plugging)
// ---------------------------------------------------------------------------

export function buildSampleStatFormula(key: string, value: number): string {
  const latex = STAT_KEY_TO_LATEX[key] ?? key;
  return `$${latex} = ${highlight(value)}$`;
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

export function fmtNum(v: number): string {
  if (Number.isInteger(v)) {
    return String(v);
  }
  return v.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
}

export function fmtP(p: number): string {
  if (p < 0.0001) {
    return '< 0.0001';
  }
  return p.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
}

function highlight(v: number): string {
  return `\\underline{\\mathbf{${fmtNum(v)}}}`;
}
